/**
 * Enable Healthcare — Design System templates (PORTED from the user-supplied zip).
 * Original: templates.jsx · 21-template catalog · full-size editable text + images.
 *
 * Conversion notes:
 *  - React-globals (const { ... } = React) replaced with ESM imports.
 *  - "assets/*" paths rewritten to "/eh-ds-assets/*" (public folder).
 *  - All component functions exported at bottom for use by the catalog.
 *  - styles.css must be imported by the parent (ContentStudio).
 */
import React, { useMemo, useRef, useState, useEffect, useContext, createContext } from "react";

// ---------- Edit context (per-template state) ----------
export const EditCtx = createContext(null);

export function useField(key, fallback) {
  const ctx = useContext(EditCtx);
  if (!ctx) return [fallback, () => {}];
  const value = (ctx.values && key in ctx.values) ? ctx.values[key] : fallback;
  const setter = (v) => ctx.set(key, v);
  return [value, setter];
}

// Enable Healthcare — Social Media Template Components
// FULLY EDITABLE: every text is contentEditable, every image is click-to-replace.
// Logos use width:auto + objectFit:contain so the lockup never stretches.


// ---------- Brand bits ----------
const PRODUCTS = {
  EHR:    { name: "Electronic Health Record", short: "EHR",   url: "ehiehr.com/ehr" },
  RCM:    { name: "Revenue Cycle Management", short: "RCM",   url: "ehiehr.com/rcm" },
  CCM:    { name: "Chronic Care Management",  short: "CCM",   url: "ehiehr.com/ccm" },
  RPM:    { name: "Remote Patient Monitoring",short: "RPM",   url: "ehiehr.com/rpm" },
  REACH:  { name: "Patient Engagement",       short: "REACH", url: "ehiehr.com/patientengagement" },
};

const LOGO_MAP = {
  "lockup-blue":   { src: "/eh-ds-assets/emblem-wordmark-blue.png",  ratio: 5800/800 },
  "lockup-white":  { src: "/eh-ds-assets/emblem-wordmark-white.png", ratio: 5800/800 },
  "wordmark-blue": { src: "/eh-ds-assets/wordmark-blue.png",         ratio: 4948/787 },
  "wordmark-white":{ src: "/eh-ds-assets/wordmark-white.png",        ratio: 4948/787 },
  "emblem-blue":   { src: "/eh-ds-assets/emblem-blue.png",           ratio: 857/800  },
  "emblem-white":  { src: "/eh-ds-assets/emblem-white.png",          ratio: 857/800  },
};

// Logo: height-driven, auto width — never stretches.
const Logo = ({ variant = "lockup", color = "blue", height = 44 }) => {
  const key = `${variant}-${color}`;
  const cfg = LOGO_MAP[key] || LOGO_MAP["lockup-blue"];
  return (
    <img
      src={cfg.src}
      alt="Enable Healthcare"
      style={{
        height,
        width: height * cfg.ratio,
        objectFit: "contain",
        display: "block",
        flexShrink: 0,
        flexGrow: 0,
      }}
    />
  );
};

// ---------- Editable text ----------
// contentEditable span; commits on blur. Single line by default.
function EditText({ fieldKey, fallback, style, multiline = false, className = "", as = "span" }) {
  const [val, setVal] = useField(fieldKey, fallback);
  const ref = useRef(null);
  const Tag = as;

  // Sync external changes (e.g. tweak panel) into the DOM without breaking caret while typing.
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.textContent = val ?? "";
    }
  }, [val]);

  return (
    <Tag
      ref={ref}
      className={`edit-text ${className}`}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onBlur={(e) => {
        const next = e.currentTarget.textContent;
        if (next !== val) setVal(next);
      }}
      onKeyDown={(e) => {
        if (!multiline && e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      style={{
        outline: "none",
        cursor: "text",
        ...style,
      }}
    >
      {val}
    </Tag>
  );
}

// ---------- Editable image ----------
// Click → file picker. Replacement stored as data URL.
function EditImage({ fieldKey, defaultSrc = null, label = "Click to replace", style, className = "", dark = false, fit = "cover", round = false }) {
  const [val, setVal] = useField(fieldKey, defaultSrc);
  const fileRef = useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setVal(reader.result);
    reader.readAsDataURL(f);
  };

  const src = val;

  return (
    <div
      className={`edit-image ${className} ${dark ? "is-dark" : ""}`}
      style={{
        position: "relative",
        cursor: "pointer",
        overflow: "hidden",
        borderRadius: round ? 9999 : (style?.borderRadius ?? 0),
        ...style,
      }}
      onClick={() => fileRef.current?.click()}
      title="Click to replace image"
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: fit, display: "block" }}
        />
      ) : (
        <div className={`imgph ${dark ? "dark" : ""}`} style={{ position: "absolute", inset: 0, borderRadius: "inherit" }}>
          <span className="imgph-label">{label}</span>
        </div>
      )}
      {/* hover affordance */}
      <div className="edit-image-hover">
        <span className="ei-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="9" cy="9" r="2"/>
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
          </svg>
        </span>
        Replace image
      </div>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick}/>
    </div>
  );
}

// ---------- Browser chrome ----------
const Browser = ({ urlField, urlFallback = "ehiehr.com/ehr", children, style }) => (
  <div className="browser" style={style}>
    <div className="chrome">
      <span className="dot r" />
      <span className="dot y" />
      <span className="dot g" />
      <div className="url">
        <EditText fieldKey={urlField} fallback={urlFallback}/>
      </div>
    </div>
    <div style={{ position: "relative" }}>{children}</div>
  </div>
);

// ===================================================================
// LAYOUT 1 — Gradient announcement
// ===================================================================
function GradientAnnouncement({ size, defaults }) {
  const L = {
    square:   { padding: 96,  eyebrow: 22, headline: 132, support: 30, gap: 40, logoH: 56 },
    portrait: { padding: 96,  eyebrow: 22, headline: 144, support: 32, gap: 44, logoH: 56 },
    story:    { padding: 110, eyebrow: 24, headline: 168, support: 36, gap: 56, logoH: 64 },
    wide:     { padding: 64,  eyebrow: 16, headline: 88,  support: 22, gap: 28, logoH: 36 },
    yt:       { padding: 120, eyebrow: 22, headline: 160, support: 32, gap: 48, logoH: 56 },
  }[size];

  return (
    <div className={`canvas size-${size} bg-navy-gradient`}>
      <div style={{ position:"absolute", inset: L.padding, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <Logo variant="lockup" color="white" height={L.logoH}/>

        <div style={{ display:"flex", flexDirection:"column", gap: L.gap, maxWidth: size==="wide" ? "70%" : "92%" }}>
          <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div"
            className="meta-text"
            style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.7)", fontWeight: 500 }}/>
          <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
            style={{ margin: 0, fontWeight: 700, fontSize: L.headline, lineHeight: 0.96, letterSpacing:"-0.035em", textWrap:"balance" }}/>
          <EditText fieldKey="support" fallback={defaults.support} as="p" multiline
            style={{ margin: 0, fontWeight: 400, fontSize: L.support, lineHeight: 1.35, color: "rgba(255,255,255,0.78)", maxWidth: size==="story" ? "85%" : "70%", letterSpacing:"-0.01em", textWrap:"pretty" }}/>
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", gap: 32 }}>
          <EditText fieldKey="cta" fallback={defaults.cta} as="div"
            style={{ fontSize: L.support * 0.85, fontWeight: 500, color:"rgba(255,255,255,0.92)" }}/>
          <EditText fieldKey="note" fallback={defaults.note} as="div"
            className="meta-text"
            style={{ fontSize: L.eyebrow * 0.8, color:"rgba(255,255,255,0.5)" }}/>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// LAYOUT 2 — Headline over photo
// ===================================================================
function HeadlineOverPhoto({ size, defaults }) {
  const isWide = size === "wide" || size === "yt";
  const L = {
    square:   { pad: 72, h: 124, eyebrow: 20, ratio: 0.55, logoH: 44 },
    portrait: { pad: 72, h: 132, eyebrow: 20, ratio: 0.58, logoH: 44 },
    story:    { pad: 90, h: 156, eyebrow: 22, ratio: 0.62, logoH: 56 },
    wide:     { pad: 56, h: 72,  eyebrow: 14, ratio: 0.5,  logoH: 32 },
    yt:       { pad: 96, h: 132, eyebrow: 20, ratio: 0.5,  logoH: 44 },
  }[size];

  if (isWide) {
    return (
      <div className={`canvas size-${size} bg-light`}>
        <div style={{ position:"absolute", inset:0, display:"grid", gridTemplateColumns:"1fr 1fr" }}>
          <div style={{ padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
            <Logo variant="lockup" color="blue" height={L.logoH}/>
            <div style={{ display:"flex", flexDirection:"column", gap: 20 }}>
              <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
                style={{ fontSize: L.eyebrow, color:"var(--eh-muted)", fontWeight: 500 }}/>
              <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
                style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 1.0, letterSpacing:"-0.03em", color:"var(--eh-navy)" }}/>
            </div>
            <EditText fieldKey="cta" fallback={defaults.cta} as="div"
              style={{ fontSize: 18, color:"var(--eh-blue)", fontWeight: 500 }}/>
          </div>
          <EditImage fieldKey="image" defaultSrc={defaults.image} label="Click to add photo"
            style={{ height: "100%" }}/>
        </div>
      </div>
    );
  }

  return (
    <div className={`canvas size-${size} bg-light`}>
      <div style={{ position:"absolute", inset:0, display:"grid", gridTemplateRows: `${L.ratio*100}% ${(1-L.ratio)*100}%` }}>
        <EditImage fieldKey="image" defaultSrc={defaults.image} label="Click to add photo" style={{ height:"100%" }}/>
        <div style={{ padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
          <div style={{ display:"flex", flexDirection:"column", gap: 22 }}>
            <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
              style={{ fontSize: L.eyebrow, color:"var(--eh-muted)", fontWeight: 500 }}/>
            <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
              style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"var(--eh-navy)", textWrap:"balance" }}/>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
            <Logo variant="lockup" color="blue" height={L.logoH}/>
            <EditText fieldKey="cta" fallback={defaults.cta} as="div"
              style={{ fontSize: 22, color:"var(--eh-blue)", fontWeight: 500 }}/>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// LAYOUT 3 — Browser hero
// ===================================================================
function BrowserHero({ size, defaults }) {
  const L = {
    square:   { pad: 80,  h: 96,  eyebrow: 18, browserH: 540, logoH: 40 },
    portrait: { pad: 80,  h: 110, eyebrow: 20, browserH: 660, logoH: 40 },
    story:    { pad: 96,  h: 128, eyebrow: 22, browserH: 920, logoH: 52 },
    wide:     { pad: 56,  h: 64,  eyebrow: 14, browserH: 380, logoH: 32 },
    yt:       { pad: 100, h: 112, eyebrow: 20, browserH: 660, logoH: 44 },
  }[size];
  const isWide = size === "wide" || size === "yt";

  const Body = (
    <Browser urlField="url" urlFallback={defaults.url} style={{ height: L.browserH, width: "100%" }}>
      {defaults.productImage !== undefined ? (
        <EditImage fieldKey="productImage" defaultSrc={defaults.productImage}
          label="Click to add product screenshot" style={{ width:"100%", height: L.browserH - 48 }} fit="cover"/>
      ) : <ProductDashboard/>}
    </Browser>
  );

  if (isWide) {
    return (
      <div className={`canvas size-${size} bg-soft`}>
        <div style={{ position:"absolute", inset:0, padding: L.pad, display:"grid", gridTemplateColumns:"1fr 1.1fr", gap: L.pad, alignItems:"center" }}>
          <div style={{ display:"flex", flexDirection:"column", gap: 28 }}>
            <Logo variant="lockup" color="blue" height={L.logoH}/>
            <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
              style={{ fontSize: L.eyebrow, color:"var(--eh-muted)", fontWeight: 500 }}/>
            <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
              style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"var(--eh-navy)", textWrap:"balance" }}/>
            <EditText fieldKey="url2" fallback={defaults.url} as="div"
              style={{ fontSize: size==="yt" ? 22 : 16, color:"var(--eh-blue)", fontWeight: 500 }}/>
          </div>
          {Body}
        </div>
      </div>
    );
  }

  return (
    <div className={`canvas size-${size} bg-soft`}>
      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <div style={{ display:"flex", flexDirection:"column", gap: 24 }}>
          <Logo variant="lockup" color="blue" height={L.logoH}/>
          <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
            style={{ fontSize: L.eyebrow, color:"var(--eh-muted)", fontWeight: 500 }}/>
          <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
            style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"var(--eh-navy)", textWrap:"balance" }}/>
        </div>
        {Body}
      </div>
    </div>
  );
}

function ProductDashboard() {
  return (
    <div style={{ background:"linear-gradient(180deg, #FBFCFE 0%, #F2F5FA 100%)", padding: "32px 40px", minHeight: 360 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color:"#1B365D", letterSpacing:"-0.02em" }}>Practice overview</div>
        <div style={{ display:"flex", gap: 10 }}>
          {["Today","Week","Month"].map((t,i)=>(
            <span key={i} style={{ padding:"6px 14px", borderRadius:999, fontSize:13, background: i===1?"#1B365D":"white", color: i===1?"white":"#6B7A90", border:"1px solid #E5EAF2", fontWeight: 500 }}>{t}</span>
          ))}
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap: 16, marginBottom: 24 }}>
        {[["Patients seen","1,284","+12%"],["Avg charting time","3:42","−38%"],["Collections","$184K","+9.2%"]].map(([l,v,d],i)=>(
          <div key={i} style={{ background:"white", border:"1px solid #E5EAF2", borderRadius:14, padding:"18px 20px" }}>
            <div style={{ fontSize:12, color:"#6B7A90", letterSpacing:"0.04em", textTransform:"uppercase", fontWeight:500 }}>{l}</div>
            <div style={{ fontSize:30, fontWeight:700, color:"#1B365D", marginTop:4, letterSpacing:"-0.02em" }}>{v}</div>
            <div style={{ fontSize:13, color:"#1B9D6A", marginTop:2, fontWeight:500 }}>{d}</div>
          </div>
        ))}
      </div>
      <div style={{ background:"white", border:"1px solid #E5EAF2", borderRadius:14, padding:20 }}>
        <div style={{ fontSize:14, color:"#6B7A90", marginBottom:14, fontWeight:500 }}>Encounters this week</div>
        <svg viewBox="0 0 600 140" width="100%" height="140" preserveAspectRatio="none">
          <defs><linearGradient id="ar" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#2E63B8" stopOpacity="0.25"/><stop offset="100%" stopColor="#2E63B8" stopOpacity="0"/></linearGradient></defs>
          <path d="M0,110 C60,80 100,90 160,60 C220,30 260,75 320,55 C380,35 420,70 480,40 C540,15 580,25 600,10 L600,140 L0,140 Z" fill="url(#ar)"/>
          <path d="M0,110 C60,80 100,90 160,60 C220,30 260,75 320,55 C380,35 420,70 480,40 C540,15 580,25 600,10" fill="none" stroke="#2E63B8" strokeWidth="2.5"/>
        </svg>
      </div>
    </div>
  );
}

// ===================================================================
// LAYOUT 4 — Product feature
// ===================================================================
function ProductFeature({ size, defaults }) {
  const L = {
    square:   { pad: 72,  h: 96,  desc: 26, eyebrow: 16, logoH: 44 },
    portrait: { pad: 80,  h: 110, desc: 28, eyebrow: 18, logoH: 44 },
    story:    { pad: 100, h: 128, desc: 32, eyebrow: 20, logoH: 56 },
    wide:     { pad: 56,  h: 64,  desc: 18, eyebrow: 13, logoH: 36 },
    yt:       { pad: 100, h: 110, desc: 26, eyebrow: 18, logoH: 44 },
  }[size];
  const isWide = size === "wide" || size === "yt";

  if (isWide) {
    return (
      <div className={`canvas size-${size} bg-deep`}>
        <div style={{ position:"absolute", inset:0, padding: L.pad, display:"grid", gridTemplateColumns:"1fr 1.2fr", gap: L.pad, alignItems:"center" }}>
          <div style={{ display:"flex", flexDirection:"column", gap: 24 }}>
            <Logo variant="lockup" color="white" height={L.logoH}/>
            <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
              style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.55)", fontWeight: 500 }}/>
            <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
              style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"white", textWrap:"balance" }}/>
            <EditText fieldKey="description" fallback={defaults.description} as="p" multiline
              style={{ margin:0, fontSize: L.desc, lineHeight: 1.4, color:"rgba(255,255,255,0.7)", maxWidth:"92%", letterSpacing:"-0.01em" }}/>
          </div>
          <EditImage fieldKey="image" defaultSrc={defaults.image} label="Click to add screenshot" dark
            style={{ aspectRatio:"4/3", borderRadius: 20 }}/>
        </div>
      </div>
    );
  }

  return (
    <div className={`canvas size-${size} bg-deep`}>
      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <Logo variant="lockup" color="white" height={L.logoH}/>
          <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
            style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.55)", fontWeight: 500 }}/>
        </div>

        <EditImage fieldKey="image" defaultSrc={defaults.image} label="Click to add screenshot" dark
          style={{ width:"100%", height: size==="story" ? 920 : size==="portrait" ? 660 : 480, borderRadius: 24 }}/>

        <div style={{ display:"flex", flexDirection:"column", gap: 16 }}>
          <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
            style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"white", textWrap:"balance" }}/>
          <EditText fieldKey="description" fallback={defaults.description} as="p" multiline
            style={{ margin:0, fontSize: L.desc, lineHeight: 1.4, color:"rgba(255,255,255,0.7)", maxWidth:"85%", letterSpacing:"-0.01em" }}/>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// LAYOUT 5 — Stat card
// ===================================================================
function StatCard({ size, defaults }) {
  const L = {
    square:   { pad: 96,  num: 360, label: 36, source: 20, logoH: 44 },
    portrait: { pad: 96,  num: 420, label: 40, source: 20, logoH: 44 },
    story:    { pad: 110, num: 540, label: 48, source: 22, logoH: 56 },
    wide:     { pad: 64,  num: 220, label: 24, source: 16, logoH: 32 },
    yt:       { pad: 120, num: 420, label: 44, source: 22, logoH: 44 },
  }[size];
  return (
    <div className={`canvas size-${size} bg-soft`}>
      <div style={{ position:"absolute", inset: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <Logo variant="lockup" color="blue" height={L.logoH}/>
        <div>
          <EditText fieldKey="stat" fallback={defaults.stat} as="div"
            style={{ fontSize: L.num, fontWeight: 700, color:"var(--eh-navy)", letterSpacing:"-0.05em", lineHeight: 0.85 }}/>
          <EditText fieldKey="label" fallback={defaults.label} as="div" multiline
            style={{ fontSize: L.label, fontWeight: 500, color:"var(--eh-navy)", marginTop: 24, lineHeight: 1.15, letterSpacing:"-0.02em", maxWidth:"85%", textWrap:"balance" }}/>
        </div>
        <EditText fieldKey="source" fallback={defaults.source} as="div" className="meta-text"
          style={{ fontSize: L.source, color:"var(--eh-muted)", fontWeight: 500 }}/>
      </div>
    </div>
  );
}

// ===================================================================
// LAYOUT 6 — Testimonial
// ===================================================================
function Testimonial({ size, defaults }) {
  const L = {
    square:   { pad: 80,  q: 64, name: 24, logoH: 40 },
    portrait: { pad: 80,  q: 70, name: 26, logoH: 40 },
    story:    { pad: 100, q: 86, name: 30, logoH: 52 },
    wide:     { pad: 56,  q: 36, name: 16, logoH: 30 },
    yt:       { pad: 100, q: 60, name: 24, logoH: 40 },
  }[size];

  const portraitSize = size==="wide" ? 220 : size==="story" ? 360 : 260;

  return (
    <div className={`canvas size-${size} bg-white`}>
      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <Logo variant="lockup" color="blue" height={L.logoH}/>
        <div style={{ display:"flex", gap: size==="wide" ? 40 : 56, alignItems:"center" }}>
          <EditImage fieldKey="image" defaultSrc={defaults.image} label="Headshot" round
            style={{ width: portraitSize, height: portraitSize, flexShrink: 0 }}/>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: L.q*1.2, color:"var(--eh-blue)", fontWeight: 700, lineHeight: 0.5, marginBottom: 8, letterSpacing:"-0.05em" }}>“</div>
            <EditText fieldKey="quote" fallback={defaults.quote} as="p" multiline
              style={{ margin:0, fontSize: L.q, fontWeight: 500, color:"var(--eh-navy)", lineHeight: 1.15, letterSpacing:"-0.025em", textWrap:"balance" }}/>
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap: 4 }}>
          <EditText fieldKey="name" fallback={defaults.name} as="div"
            style={{ fontSize: L.name, fontWeight: 700, color:"var(--eh-navy)", letterSpacing:"-0.01em" }}/>
          <div style={{ fontSize: L.name*0.75, color:"var(--eh-muted)", fontWeight: 400 }}>
            <EditText fieldKey="role" fallback={defaults.role} as="span"/>
            <span> · </span>
            <EditText fieldKey="practice" fallback={defaults.practice} as="span"/>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// LAYOUT 7 — Carousel slide
// ===================================================================
function CarouselSlide({ size, slideNumber, totalSlides = 5, defaults }) {
  const L = {
    square:   { pad: 96,  title: 110, body: 28, ind: 18, logoH: 44 },
    portrait: { pad: 96,  title: 124, body: 30, ind: 20, logoH: 44 },
    story:    { pad: 110, title: 156, body: 34, ind: 22, logoH: 56 },
    wide:     { pad: 56,  title: 56,  body: 18, ind: 14, logoH: 30 },
    yt:       { pad: 110, title: 132, body: 28, ind: 20, logoH: 44 },
  }[size];

  const isFirst = slideNumber === 1;

  return (
    <div className={`canvas size-${size} ${isFirst ? "bg-navy-gradient" : "bg-light"}`}>
      <div style={{ position:"absolute", inset: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <Logo variant="lockup" color={isFirst ? "white" : "blue"} height={L.logoH}/>
          <div className="meta-text" style={{ fontSize: L.ind, color: isFirst ? "rgba(255,255,255,0.6)" : "var(--eh-muted)", fontWeight: 500, fontVariantNumeric:"tabular-nums" }}>
            {String(slideNumber).padStart(2,"0")} / {String(totalSlides).padStart(2,"0")}
          </div>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap: 36 }}>
          <EditText fieldKey="kicker" fallback={defaults.kicker} as="div" className="meta-text"
            style={{ fontSize: L.ind, color: isFirst ? "rgba(255,255,255,0.7)" : "var(--eh-blue)", fontWeight: 500 }}/>
          <EditText fieldKey="title" fallback={defaults.title} as="h1" multiline
            style={{ margin:0, fontSize: L.title, fontWeight: 700, lineHeight: 0.95, letterSpacing:"-0.035em", textWrap:"balance",
                     color: isFirst ? "white" : "var(--eh-navy)" }}/>
          <EditText fieldKey="body" fallback={defaults.body} as="p" multiline
            style={{ margin:0, fontSize: L.body, lineHeight: 1.4, maxWidth:"85%", letterSpacing:"-0.01em",
                     color: isFirst ? "rgba(255,255,255,0.78)" : "var(--eh-muted)" }}/>
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <EditText fieldKey="cta" fallback={slideNumber === totalSlides ? "ehiehr.com →" : "Swipe →"} as="div"
            style={{ fontSize: L.ind*0.95, fontWeight: 500, color: isFirst ? "rgba(255,255,255,0.85)" : "var(--eh-blue)" }}/>
          <div style={{ display:"flex", gap: 6 }}>
            {Array.from({length: totalSlides}).map((_,i)=>(
              <span key={i} style={{ width: 22, height: 4, borderRadius: 2,
                background: i < slideNumber ? (isFirst ? "white" : "var(--eh-navy)") : (isFirst ? "rgba(255,255,255,0.25)" : "rgba(27,54,93,0.2)") }}/>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// LAYOUT 8 — Event promo
// ===================================================================
function EventPromo({ size, defaults }) {
  const L = {
    square:   { pad: 80,  title: 96,  date: 32, body: 20, logoH: 44 },
    portrait: { pad: 80,  title: 108, date: 36, body: 22, logoH: 44 },
    story:    { pad: 110, title: 132, date: 40, body: 24, logoH: 56 },
    wide:     { pad: 56,  title: 56,  date: 22, body: 16, logoH: 32 },
    yt:       { pad: 100, title: 110, date: 36, body: 22, logoH: 44 },
  }[size];

  return (
    <div className={`canvas size-${size} bg-deep`}>
      <svg style={{ position:"absolute", right: -200, top: -200, opacity: 0.15 }} width="900" height="900" viewBox="0 0 900 900">
        <circle cx="450" cy="450" r="380" stroke="white" strokeWidth="1" fill="none"/>
        <circle cx="450" cy="450" r="280" stroke="white" strokeWidth="1" fill="none"/>
        <circle cx="450" cy="450" r="180" stroke="white" strokeWidth="1" fill="none"/>
      </svg>

      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <Logo variant="lockup" color="white" height={L.logoH}/>
          <div className="meta-text" style={{ fontSize: L.body*0.85, color:"#6FB8FF", fontWeight: 500, padding:"8px 16px", border:"1px solid rgba(111,184,255,0.4)", borderRadius: 999 }}>
            ● Live · <EditText fieldKey="eventType" fallback={defaults.eventType} as="span"/>
          </div>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap: 28 }}>
          <div className="meta-text" style={{ fontSize: L.body, color:"rgba(255,255,255,0.6)", fontWeight: 500 }}>
            <EditText fieldKey="date" fallback={defaults.date} as="span"/>
            <span> · </span>
            <EditText fieldKey="time" fallback={defaults.time} as="span"/>
          </div>
          <EditText fieldKey="title" fallback={defaults.title} as="h1" multiline
            style={{ margin:0, fontSize: L.title, fontWeight: 700, lineHeight: 0.95, letterSpacing:"-0.035em", textWrap:"balance", color:"white" }}/>
          <div style={{ display:"flex", alignItems:"center", gap: 16, marginTop: 8 }}>
            <EditImage fieldKey="presenterImage" defaultSrc={null} label="" round
              style={{
                width: L.body*2.4, height: L.body*2.4, flexShrink: 0,
                background: "linear-gradient(135deg, #2E63B8, #6FB8FF)",
              }}/>
            <div>
              <EditText fieldKey="presenter" fallback={defaults.presenter} as="div"
                style={{ fontSize: L.body*1.05, fontWeight: 700, color:"white" }}/>
              <EditText fieldKey="role" fallback={defaults.role} as="div"
                style={{ fontSize: L.body*0.85, color:"rgba(255,255,255,0.6)" }}/>
            </div>
          </div>
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap: 12, padding: `${L.body*0.7}px ${L.body*1.4}px`, background:"white", borderRadius: 999 }}>
            <EditText fieldKey="cta" fallback={defaults.cta} as="span"
              style={{ color:"var(--eh-navy)", fontSize: L.body, fontWeight: 700, letterSpacing:"-0.01em" }}/>
            <span style={{ color:"var(--eh-navy)" }}>→</span>
          </div>
          <EditText fieldKey="eventUrl" fallback={defaults.eventUrl} as="div" className="meta-text"
            style={{ fontSize: L.body*0.8, color:"rgba(255,255,255,0.5)" }}/>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// LAYOUT 9 — Multi-device hero
// ===================================================================
function DeviceHero({ size, defaults }) {
  const L = {
    square:   { pad: 80,  h: 110, eyebrow: 18, support: 22, logoH: 44 },
    portrait: { pad: 80,  h: 124, eyebrow: 20, support: 24, logoH: 44 },
    story:    { pad: 100, h: 148, eyebrow: 22, support: 28, logoH: 56 },
    wide:     { pad: 56,  h: 64,  eyebrow: 14, support: 16, logoH: 36 },
    yt:       { pad: 100, h: 110, eyebrow: 18, support: 22, logoH: 44 },
  }[size];
  const isWide = size === "wide" || size === "yt";

  if (isWide) {
    return (
      <div className={`canvas size-${size} bg-navy-gradient`}>
        <div style={{ position:"absolute", inset:0, padding: L.pad, display:"grid", gridTemplateColumns:"1fr 1.2fr", gap: L.pad, alignItems:"center" }}>
          <div style={{ display:"flex", flexDirection:"column", gap: 22 }}>
            <Logo variant="lockup" color="white" height={L.logoH}/>
            <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
              style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.65)", fontWeight: 500 }}/>
            <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
              style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"white", textWrap:"balance" }}/>
            <EditText fieldKey="support" fallback={defaults.support} as="p" multiline
              style={{ margin:0, fontSize: L.support, color:"rgba(255,255,255,0.75)", lineHeight: 1.4, letterSpacing:"-0.01em" }}/>
          </div>
          <EditImage fieldKey="deviceImage" defaultSrc={defaults.deviceImage} label="Device mockup"
            fit="contain"
            style={{ width:"100%", aspectRatio: "16/9", filter:"drop-shadow(0 30px 60px rgba(0,0,0,0.4))" }}/>
        </div>
      </div>
    );
  }

  return (
    <div className={`canvas size-${size} bg-navy-gradient`}>
      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <div style={{ display:"flex", flexDirection:"column", gap: 22 }}>
          <Logo variant="lockup" color="white" height={L.logoH}/>
          <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
            style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.65)", fontWeight: 500 }}/>
          <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
            style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"white", textWrap:"balance" }}/>
          <EditText fieldKey="support" fallback={defaults.support} as="p" multiline
            style={{ margin:0, fontSize: L.support, color:"rgba(255,255,255,0.75)", lineHeight: 1.4, maxWidth:"80%", letterSpacing:"-0.01em" }}/>
        </div>
        <EditImage fieldKey="deviceImage" defaultSrc={defaults.deviceImage} label="Device mockup"
          fit="contain"
          style={{ width:"100%", aspectRatio: "16/9", filter:"drop-shadow(0 30px 60px rgba(0,0,0,0.4))" }}/>
      </div>
    </div>
  );
}

// ---------- Exports ----------
export {
  Logo, EditText, EditImage, Browser,
  GradientAnnouncement, HeadlineOverPhoto, BrowserHero, ProductDashboard,
  ProductFeature, StatCard, Testimonial, CarouselSlide, EventPromo, DeviceHero,
};
